/**
 * Ce que l'onglet activité dit d'un écrit sur un contrat BeSTT.
 *
 * Ces événements viennent de la collection `contractevents`, pas du journal de
 * mission : le balayage nocturne clôture sans auteur, et le journal exige un
 * utilisateur. Leur type est en majuscules, ce qui les distingue à la lecture
 * des événements historiques.
 */

const CONTRACT_EVENT_LABELS = {
  CONTRACT_CREATED: {
    applied: 'a généré le contrat sur BeSTT',
    refused: "n'a pas pu générer le contrat sur BeSTT",
  },
  AMENDMENT_CREATED: {
    applied: 'a généré un avenant de prolongation sur BeSTT',
    refused: "n'a pas pu générer l'avenant de prolongation sur BeSTT",
  },
  VALUE_AMENDMENT_CREATED: {
    applied: 'a généré un avenant de valeur sur BeSTT',
    refused: "n'a pas pu générer l'avenant de valeur sur BeSTT",
  },
  CONTRACT_PATCHED: {
    applied: 'a mis à jour le contrat sur BeSTT',
    refused: "n'a pas pu mettre à jour le contrat sur BeSTT",
  },
  BONUSES_PROPAGATED: {
    applied: 'a reporté les primes de l’entreprise sur le contrat',
    refused: "n'a pas pu reporter les primes de l’entreprise",
  },
  CONTRACT_CLOSED: {
    applied: 'a clôturé le contrat sur BeSTT',
    refused: "n'a pas pu clôturer le contrat sur BeSTT",
  },
  CONTRACT_ARCHIVED: {
    applied: 'a sorti le contrat de la mission — à supprimer dans BeSTT',
    refused: "n'a pas pu sortir le contrat de la mission",
  },
  // Yousign, not BeSTT: the recipient completes the label (§ PARTY_LABELS),
  // each party signing their own copy.
  SIGNATURE_SENT: {
    applied: 'a envoyé le contrat en signature',
    refused: "n'a pas pu envoyer le contrat en signature",
  },
  SIGNATURE_SIGNED: {
    // Nobody at Jobbiz signs: we merely record that the signature came back.
    applied: 'a reçu la signature',
    refused: "n'a pas pu enregistrer la signature",
  },
  SIGNATURE_LINK_RENEWED: {
    applied: 'a renouvelé le lien de signature',
    refused: "n'a pas pu renouveler le lien de signature",
  },
  SIGNATURE_EXPIRED: {
    applied: 'a constaté que la demande de signature a expiré',
    refused: "n'a pas pu enregistrer l'expiration de la demande de signature",
  },
  SIGNATURE_CANCELED: {
    applied: 'a constaté que la demande de signature a été annulée',
    refused: "n'a pas pu enregistrer l'annulation de la demande de signature",
  },
};

/** Whose copy it is. */
const PARTY_LABELS = {
  tempworker: "l'intérimaire",
  company: "l'entreprise",
};

const SIGNATURE_ACTIONS = [
  'SIGNATURE_SENT',
  'SIGNATURE_SIGNED',
  'SIGNATURE_LINK_RENEWED',
  'SIGNATURE_EXPIRED',
  'SIGNATURE_CANCELED',
];

/** Who or what produced the line (`details.source`). */
const SOURCE_LABELS = {
  'yousign-webhook': 'YouSign',
  'mobile-interim': 'application intérimaire',
};

const contractSourceLabel = (source) => SOURCE_LABELS[source] ?? source ?? '';

/**
 * Why an amendment was created rather than the contract updated in place.
 *
 * The decision is made far from the write, in updateMissionDetails: read
 * later, an amendment says what it carries, never what made it necessary.
 */
const AMENDMENT_TRIGGER_LABELS = {
  imported_hours: 'des heures étaient déjà importées sur BeSTT',
  contract_signed: 'le contrat était déjà signé',
  dpae_sent: 'la DPAE était déjà partie',
  rate_change: 'le taux horaire a changé, et un contrat garde le sien',
  start_date_change: 'la date de début a changé',
  amendment_request_accepted: 'une demande de prolongation a été acceptée',
  regenerated: "la première génération sur BeSTT avait échoué",
};

const amendmentTriggerLabel = (activity) => {
  const trigger = activity?.details?.trigger;
  if (!trigger) return '';
  // A motif unknown to the front end is shown as-is: hiding it would only
  // bring back the question this line exists to answer.
  return AMENDMENT_TRIGGER_LABELS[trigger] ?? trigger;
};

/** Les motifs de refus, dans les mots de l'API. */
const REFUSAL_LABELS = {
  contract_unreadable: 'contrat illisible sur BeSTT',
  key_not_found: 'clé BeSTT introuvable',
  bestt_refused: 'refusé par BeSTT',
  yousign_refused: 'refusé par YouSign',
  document_unavailable: 'document introuvable',
  forbidden: 'accès refusé',
  request_mismatch: 'demande différente de celle en cours',
  yousign_unavailable: 'YouSign indisponible',
  signed_pdf_unavailable: 'PDF signé indisponible',
  storage_failed: 'dépôt du PDF échoué',
  replaced_by_resend: 'remplacée par un renvoi',
  canceled_outside_resend: 'annulée hors renvoi',
};

/**
 * Motifs qui précisent une ligne `applied` : une annulation constatée n'est
 * pas un échec, elle dit seulement pourquoi la demande a disparu.
 */
const APPLIED_REASONS = ['replaced_by_resend', 'canceled_outside_resend'];

const contractAppliedNote = (activity) => {
  const details = activity?.details ?? {};
  if (details.status === 'refused' || !APPLIED_REASONS.includes(details.reason)) {
    return '';
  }
  return REFUSAL_LABELS[details.reason];
};

/** L'état qu'un geste laisse au contrat, quand il en change un. */
const STATUS_AFTER = {
  CONTRACT_CREATED: { before: null, after: 'Généré' },
  AMENDMENT_CREATED: { before: null, after: 'Avenant généré' },
  VALUE_AMENDMENT_CREATED: { before: null, after: 'Avenant généré' },
  CONTRACT_CLOSED: { before: 'Ouvert', after: 'Clôturé' },
  SIGNATURE_SENT: { before: null, after: 'Envoyé en signature' },
  SIGNATURE_SIGNED: { before: 'Envoyé en signature', after: 'Signé' },
  SIGNATURE_EXPIRED: { before: 'Envoyé en signature', after: 'Expiré' },
  SIGNATURE_CANCELED: { before: 'Envoyé en signature', after: 'Annulé' },
};

const isContractEvent = (type) =>
  Boolean(type) && Object.prototype.hasOwnProperty.call(CONTRACT_EVENT_LABELS, type);

const contractDocumentLabel = (contract) =>
  contract?.avenant_number ? `avenant ${contract.avenant_number}` : 'contrat';

/**
 * Une clôture trouvée déjà faite sur BeSTT n'est pas un geste de Jobbiz : le
 * balayage a lu l'état et l'a recopié, il n'a rien écrit. La présenter comme une
 * clôture ferait croire que la date de fin affichée vient de nous.
 */
const ALREADY_CLOSED_LABEL =
  'a constaté que le contrat était déjà clôturé sur BeSTT';

/** A closure landing this close to its generation is the same gesture. */
const CLOSURE_WINDOW_MS = 60_000;

/**
 * Folds a closure into the generation that carried it.
 *
 * A mission interrupted before it had a contract has its closure deferred: the
 * document is created, then closed three seconds later, in one gesture. Two
 * lines for it read as two decisions, and the second one — « a clôturé le
 * contrat » — suggests somebody went and did it.
 *
 * Only for a closure that lands within the minute, on the same document, and
 * that BeSTT accepted: a refusal is what an admin has to catch up on, and the
 * nightly sweep closes months later.
 */
const foldGenerationClosure = (activities = []) => {
  const generations = new Map();
  for (const activity of activities) {
    const numero = activity?.details?.contract?.numero;
    if (activity?.type === 'CONTRACT_CREATED' && numero) {
      generations.set(numero, activity);
    }
  }

  const kept = [];
  for (const activity of activities) {
    const details = activity?.details ?? {};
    if (activity?.type === 'CONTRACT_CLOSED' && details.status !== 'refused') {
      const generation = generations.get(details.contract?.numero);
      if (
        generation &&
        Math.abs(new Date(activity.date) - new Date(generation.date)) <=
          CLOSURE_WINDOW_MS
      ) {
        generation._closedWith = activity;
        continue;
      }
    }
    kept.push(activity);
  }
  return kept;
};

const contractEventText = (activity) => {
  const details = activity?.details ?? {};

  // Archived or left on the card: it is not the same work waiting for the
  // admin in BeSTT.
  if (activity?.type === 'CONTRACT_ARCHIVED' && details.sent?.archived === false) {
    return {
      label: 'a laissé le contrat sur la mission — à clôturer dans BeSTT',
      extra: `— ${contractDocumentLabel(details.contract)}`,
    };
  }

  // Generated then closed in one gesture: one sentence for it.
  if (activity?._closedWith) {
    return {
      label: 'a généré et clôturé le contrat sur BeSTT',
      extra: `— ${contractDocumentLabel(details.contract)}${
        details.contract?.numero ? ` n°${details.contract.numero}` : ''
      }`,
    };
  }

  const refused = details.status === 'refused';
  const labels = CONTRACT_EVENT_LABELS[activity?.type] ?? {};
  let label =
    details.reason === 'already_closed'
      ? ALREADY_CLOSED_LABEL
      : refused
        ? labels.refused
        : labels.applied;

  // « … en signature » and « … la signature » both call for the recipient:
  // without it, two sends for the same contract are unreadable.
  if (SIGNATURE_ACTIONS.includes(activity?.type)) {
    const party = PARTY_LABELS[details.party];
    if (party) {
      const preposition =
        activity.type === 'SIGNATURE_SIGNED'
          ? 'de'
          : activity.type === 'SIGNATURE_SENT'
            ? 'à'
            : 'pour';
      label = `${label} ${preposition} ${party}`;
    }
  }

  const parts = [contractDocumentLabel(details.contract)];
  if (details.contract?.numero) parts.push(`n°${details.contract.numero}`);
  if (refused) {
    // Un motif inconnu du front est montré tel quel : le taire ferait passer un
    // échec pour un geste sans conséquence.
    parts.push(REFUSAL_LABELS[details.reason] ?? details.reason ?? 'refusé');
  } else if (contractAppliedNote(activity)) {
    parts.push(contractAppliedNote(activity));
  }

  return { label: label ?? activity?.type, extra: `— ${parts.join(' ')}` };
};

const contractStatusChange = (activity) => {
  // One gesture, so one state: striking « Généré » through would suggest the
  // contract lived open for a while first.
  if (activity?._closedWith) return { before: null, after: 'Généré et clôturé' };
  if (activity?.details?.status === 'refused') return null;
  if (activity?.details?.reason === 'already_closed') {
    return { before: null, after: 'Déjà clôturé' };
  }
  return STATUS_AFTER[activity?.type] ?? null;
};

/**
 * BeSTT rend ses dates en `2026-08-03`, Jobbiz les lui envoie en `20260803` :
 * les deux se croisent dans le même journal. Affichées brutes, elles ne se
 * comparent pas à l'œil.
 */
const formatBesttDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const digits = String(value).replace(/-/g, '');
  const match = digits.match(/^(\d{4})(\d{2})(\d{2})/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

export {
  amendmentTriggerLabel,
  foldGenerationClosure,
  formatBesttDate,
  isContractEvent,
  contractDocumentLabel,
  contractAppliedNote,
  contractEventText,
  contractStatusChange,
  contractSourceLabel,
  CONTRACT_EVENT_LABELS,
};
